import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@/lib/api";
import { useTrack } from "@/lib/track";
import { Button } from "@/components/ui/button";

interface Membership {
  role: "member" | "leader" | "moderator";
  status: "active" | "pending";
}

interface Props {
  circleId: string;
  circleSlug: string;
  isPrivate: boolean;
  membership: Membership | null;
}

/**
 * State machine for the primary circle action:
 *   - not joined, public   → "Join circle"   (primary)
 *   - not joined, private  → "Request access" (outline)
 *   - pending              → "Request pending" (disabled, outline)
 *   - active member        → "Leave circle"  (ghost, monochrome)
 *   - active leader/mod    → role label, no action (manage UI is Phase 7)
 */
export function JoinButton({ circleId, circleSlug, isPrivate, membership }: Props) {
  const api = useApi();
  const qc = useQueryClient();
  const track = useTrack();

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["circle", circleSlug] });
    qc.invalidateQueries({ queryKey: ["circles"] });
  };

  const join = useMutation({
    mutationFn: () => api.post<{ status: string; role: string }>(`/circles/${circleId}/join`),
    onSuccess: (data) => {
      track("circle.join", { is_private: isPrivate, status: data?.status ?? "active" });
      refresh();
    },
  });
  const leave = useMutation({
    mutationFn: () => api.del(`/circles/${circleId}/leave`),
    onSuccess: refresh,
  });

  if (membership?.status === "active") {
    if (membership.role === "leader" || membership.role === "moderator") {
      return (
        <div className="text-[10px] uppercase tracking-[0.18em] text-gold-glow">
          You're a {membership.role} here
        </div>
      );
    }
    return (
      <Button
        variant="ghost"
        size="sm"
        onClick={() => leave.mutate()}
        disabled={leave.isPending}
      >
        {leave.isPending ? "Leaving…" : "Leave circle"}
      </Button>
    );
  }

  if (membership?.status === "pending") {
    return (
      <Button variant="outline" size="sm" disabled>
        Request pending
      </Button>
    );
  }

  return (
    <Button
      variant={isPrivate ? "outline" : "primary"}
      size="sm"
      onClick={() => join.mutate()}
      disabled={join.isPending}
    >
      {join.isPending ? (isPrivate ? "Requesting…" : "Joining…") : isPrivate ? "Request access" : "Join circle"}
    </Button>
  );
}
